import React, { useState, useEffect, useCallback, useRef } from 'react';
import PieChart from './charts/pieChart';
import MultiLineChartTwo from './charts/multiLineChartTwo';
import QualityFeaturesChart from './charts/QualityFeaturesChart';
import BarChartTwo from './charts/barChartTwo';
import Histogram from './charts/histogram';
import { Card, Chip } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ButtonGroupCompression from './ButtonGroup/ButtonGroupCompression';
import ButtonGroupProjections from './ButtonGroup/ButtonGroupProjections';
import ButtonGroupQualityFeats from './ButtonGroup/ButtonGroupPGMI';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as actions from '../../../store/index';
import { formatDate, daysInMonth } from '../../../helpers/formatDate';
import { Helmet } from 'react-helmet';
import MultilineChart from './charts/multilineChart';
import ButtonGroupDiagnostics from './ButtonGroup/ButtonGroupDiagnostics';
import ButtonGroupQuality from './ButtonGroup/ButtonGroupQuality';
import ChartCard from './ChartCard/ChartCard';
import Copyright from './Copyright';
import { diagnosticsClassColor } from '../../../helpers/manageColors';

const useStyles = makeStyles((theme) => ({
  dashboard_general: {
    height: '100%',
    width: '100%',
    display: 'grid',
    gridRowGap: '10px',
    gridColumnGap: '10px',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gridTemplateRows: 'repeat(3, 1fr)',
    justifyItems: 'stretch',
    padding: '8px',
  },

  dashboard_responsive: {
    gridTemplateColumns: 'repeat(6, 1fr)',
    gridTemplateRows: 'repeat(7, 1fr)',
    display: 'grid',
    gridRowGap: '6px',
    gridColumnGap: '6px',
    justifyItems: 'stretch',
    width: '100%',
    padding: '8px 8px 3vh 8px',
  },

  icon: {
    fontSize: '3vh',
  },

  CardContent: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
    height: '80%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  gridRowOneRanking: {
    padding: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },

  InfoIcon: {
    padding: 0,
    color: 'gray',
    fontSize: '1rem',
  },

  featuresFilter: {
    letterSpacing: '0.1rem',
    maxHeight: '20px',
    textTransform: 'capitalize',
    fontSize: '0.7em',
    display: 'flex',
    margin: '0 5%',
    padding: '4px',
    '& p': {
      color: theme.palette.secondary.main,
      margin: 0,
    },
  },

  smalleaturesFilter: {
    letterSpacing: '0.1rem',
    maxHeight: '20px',
    textTransform: 'capitalize',
    fontSize: '0.5em',
    display: 'flex',
    margin: '1px',
    padding: '2px',
    '& p': {
      color: theme.palette.secondary.main,
      margin: 0,
    },
  },

  title_header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
}));

const Dashboard = () => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // Store State
  const { totalExaminations, currentlyProcessed } = useSelector((state) => state.examinations);
  const gui = useSelector((state) => state.gui);
  const dates = useSelector((state) => state.dates);
  const filter = useSelector((state) => state.filter);
  const averageDose = useSelector((state) => state.statistics.doseData.aver);
  const { average_per_projection, average_per_study } = useSelector(
    (state) => state.statistics.performanceHistogramData,
  );
  const barQualityData = useSelector((state) => state.statistics.barQualityData);
  const examinationListDrawer = useSelector((state) => state.gui.examinationListDrawer);
  const globalSettings = useSelector((state) => state.global_settings ?? {});

  // Loading states
  const loadingExams = useSelector((state) => state.statistics.loadingExams);
  const qualityStudiesData = useSelector((state) => state.statistics.pieStudyQualityData);
  const loadingDensity = useSelector((state) => state.statistics.loadingDensity);
  const loadingPerformance = useSelector((state) => state.statistics.loadingPerformance);
  const loadingCompression = useSelector((state) => state.statistics.loadingCompression);
  const loadingDose = useSelector((state) => state.statistics.loadingDose);
  const loadingFeatures = useSelector((state) => state.statistics.loadingFeatures);
  const loadingQuality = useSelector((state) => state.statistics.loadingQuality);
  const loadingRisk = useSelector((state) => state.statistics.loadingRisk);
  const loadingOpacities = useSelector((state) => state.statistics.loadingOpacities);
  const loadingMicrocalc = useSelector((state) => state.statistics.loadingMicrocalc);

  // Local state
  const [qualityFeatProjection, setQualityFeatProjection] = useState('rcc');
  const [cmpProjections, setCmpProjections] = useState([0, 1, 2, 3]);
  const [diagnosticCategory, setDiagnosticCategory] = useState('opacities');
  const [qualityData, setQualityData] = useState('projections');
  const [performanceProjections, setPerformanceProjections] = useState(['rcc', 'lcc', 'rmlo', 'lmlo']);

  const prevTotalExaminationsRef = useRef(totalExaminations);
  const prevFilterRef = useRef();

  const { diagnostics_microcalc_module, diagnostics_opacities_module, density_module, risk_module } =
    globalSettings.premium_features;
  const { compression_low, compression_high, compression_tolerance } = globalSettings;
  const quality_values = globalSettings?.quality_system?.quality_values;

  const diagnostics_module =
    (diagnostics_microcalc_module || diagnostics_opacities_module) &&
    localStorage.getItem('diagnostics') === 'true';

  const histogram_chart = globalSettings?.quality_system?.histogram_chart;

  // Effects
  useEffect(() => {
    if (diagnostics_opacities_module) setDiagnosticCategory('opacities');
    else if (diagnostics_microcalc_module) setDiagnosticCategory('microcalc');
    dispatch(actions.handleRevisionDiscard());
  }, []);

  useEffect(() => {
    dispatch(actions.setCurrentExam(null));
    return () => {
      dispatch(actions.setInitialStatistics());
    };
  }, [dispatch]);

  useEffect(() => {
    const filterChanged = prevFilterRef.current === undefined || prevFilterRef.current !== filter;
    prevFilterRef.current = filter;

    const prevTotal = prevTotalExaminationsRef.current;
    const totalChanged = prevTotal !== totalExaminations;
    prevTotalExaminationsRef.current = totalExaminations;

    if (filterChanged) {
      const abortMostStats = new AbortController();
      dispatch(actions.fetchMostStatistics(theme, t, abortMostStats.signal));
      return () => abortMostStats.abort();
    }

    if ((totalChanged || currentlyProcessed) && window.location.pathname.includes('dashboard')) {
      const abortMostStats = new AbortController();
      const delayMs = 6000;
      const timerId = window.setTimeout(() => {
        dispatch(actions.fetchMostStatistics(theme, t, abortMostStats.signal));
      }, delayMs);
      return () => {
        window.clearTimeout(timerId);
        abortMostStats.abort();
      };
    }
  }, [filter, currentlyProcessed, totalExaminations]);

  useEffect(() => {
    const abortPerfStats = new AbortController();
    dispatch(actions.fetchPerformanceAction(abortPerfStats.signal));
    return () => abortPerfStats.abort();
  }, [dispatch, filter, dates.performanceDates]);

  useEffect(() => {
    const abortCompStats = new AbortController();
    dispatch(actions.fetchCompressionAction(abortCompStats.signal));
    return () => abortCompStats.abort();
  }, [dispatch, filter, dates.compressionDates]);

  // Handlers
  function setActiveProjCompression(n, btn_id) {
    const button = document.getElementById(btn_id);

    const cmp_projections = cmpProjections.filter((value) => value !== n);

    if (button.className.split(' ').some((c) => /MuiButton-contained.*/.test(c))) {
    } else {
      cmp_projections.push(n);
    }

    setCmpProjections(cmp_projections);
  }

  function setActiveQualityProjection(projection) {
    // Helper to check if projection is a CC or MLO
    const isCC = (p) => p && (p.toLowerCase() === 'rcc' || p.toLowerCase() === 'lcc');
    const isMLO = (p) => p && (p.toLowerCase() === 'rmlo' || p.toLowerCase() === 'lmlo');

    if (qualityFeatProjection === 'cc' && (projection === 'rmlo' || projection === 'lmlo'))
      setQualityFeatProjection(projection);
    if (qualityFeatProjection === 'mlo' && (projection === 'rcc' || projection === 'lcc'))
      setQualityFeatProjection(projection);
    else if (
      isCC(projection) &&
      isCC(qualityFeatProjection) &&
      projection.toLowerCase() !== qualityFeatProjection.toLowerCase()
    ) {
      setQualityFeatProjection('cc');
      return;
    } else if (
      isMLO(projection) &&
      isMLO(qualityFeatProjection) &&
      projection.toLowerCase() !== qualityFeatProjection.toLowerCase()
    ) {
      setQualityFeatProjection('mlo');
      return;
    } else if (qualityFeatProjection === 'cc' && (projection === 'rcc' || projection === 'lcc')) {
      projection === 'rcc' ? setQualityFeatProjection('lcc') : setQualityFeatProjection('rcc');
    } else if (qualityFeatProjection === 'mlo' && (projection === 'rmlo' || projection === 'lmlo')) {
      projection === 'rmlo' ? setQualityFeatProjection('lmlo') : setQualityFeatProjection('rmlo');
    } else {
      setQualityFeatProjection(projection);
    }
  }

  function activePerformanceProj(proj) {
    setPerformanceProjections((performanceProjections) => {
      if (performanceProjections.includes(proj)) {
        return performanceProjections.filter((value) => value !== proj);
      } else {
        return [...performanceProjections, proj];
      }
    });
  }

  function featuresQualityHandler(label, feature, value) {
    const featuresQuality = {
      label,
      feature,
      value,
      projection: qualityFeatProjection.toUpperCase(),
    };
    dispatch(actions.setFilterProperty(['featuresQuality', featuresQuality]));
  }

  function formatAveragePerformance() {
    const { avg_performance: avgPerfProj, avg_points: avgPointsProj } = average_per_projection || {};
    const { avg_performance: avgPerfStudy, avg_points: avgPointsStudy } = average_per_study || {};
    if (!avgPerfProj || !avgPointsProj || !avgPerfStudy || !avgPointsStudy) return '';

    const perProj = `${t('Dashboard.per_projection')} ${avgPerfProj.toFixed(2)}% (${avgPointsProj.toFixed(
      2,
    )} ${t('Dashboard.points')})`;
    const perStudy = `${t('Dashboard.per_study')} ${avgPerfStudy.toFixed(2)}% (${avgPointsStudy.toFixed(
      2,
    )} ${t('Dashboard.points')})`;

    return `${perProj} | ${perStudy}`;
  }

  const filterChartHandler = useCallback(
    (feature, chart) => {
      if (chart === 'pieDensityData') {
        dispatch(actions.setFilterProperty(['density', feature]));
      } else if (chart === 'pieQualityData') {
        dispatch(actions.setFilterProperty(['quality', feature]));
      } else if (chart === 'pieStudyQualityData') {
        dispatch(actions.setFilterProperty(['study_quality', feature]));
      } else if (chart === 'microcalc') {
        dispatch(actions.setFilterProperty(['microcalc', feature]));
      } else if (chart === 'opacities') {
        dispatch(actions.setFilterProperty(['opacities', feature]));
      } else if (chart === 'pieRiskData') {
        dispatch(actions.setFilterProperty(['risk', feature]));
      }
    },
    [filter, dispatch],
  );

  const onPerformanceBulletClick = (data) => {
    if (data.uid !== undefined) dispatch(actions.showExamination(data.uid));
    else if (data.date !== undefined) {
      let from = formatDate(data.date).trim();
      let to = from;
      if (from.length < 8) {
        from = from + '-01';
        to = to + '-' + daysInMonth(to.slice(5, 7), to.slice(0, 4));
      }
      dispatch(actions.setPerformanceDates([from, to]));
    }
  };

  const onCompressionBulletClick = (data) => {
    if (data.uid !== undefined) dispatch(actions.showExamination(data.uid));
    else if (data.date !== undefined) {
      let from = formatDate(data.date).trim();
      let to = from;
      if (from.length < 8) {
        from = from + '-01';
        to = to + '-' + daysInMonth(to.slice(5, 7), to.slice(0, 4));
      }
      dispatch(actions.setCompressionDates([from, to]));
    }
  };

  const getChipLabel = (chart) => {
    if (chart === 'compression') {
      if (dates.compressionDates[0] === dates.compressionDates[1]) return dates.compressionDates[0];

      if (dates.compressionDates[0] !== dates.compressionDates[1])
        return `${dates.compressionDates[0]} -
                            ${dates.compressionDates[1]}`;
    }
    if (chart === 'performance') {
      if (dates.performanceDates[0] === dates.performanceDates[1]) return dates.performanceDates[0];

      if (dates.performanceDates[0] !== dates.performanceDates[1])
        return `${dates.performanceDates[0]} -
                            ${dates.performanceDates[1]}`;
    }
  };

  const pgmiChipColor = (value) => {
    if (value === 'none') return theme.palette.dashboard.noSkinFolds;
    if (
      value === 'correct' ||
      value === 'mild' ||
      value === 'straight' ||
      value === 'mild' ||
      value === 'convex'
    )
      return theme.palette.projections.good;
    if (value === 'moderate' || value === 'medium') return theme.palette.projections.moderate;
    if (value === 'insufficient' || value === 'severe' || value === 'concave')
      return theme.palette.projections.insufficient;
  };

  const qualityChipLabel = (label) => {
    if (!label[0] || label[0] == 'null' || label[0].toLowerCase() === 'not_eval') {
      return t(`Dashboard.not_eval`);
    }
    return t(`Quality.${label[0]}`).toUpperCase();
  };

  const diagnosticChipLabel = (label) => {
    if (!label[0] || label[0] == 'null' || label[0].toLowerCase() === 'not_eval') {
      return t(`Dashboard.not_eval`);
    } else if (label[0] === 'none') {
      return t(`projections.birads1`).toUpperCase();
    } else if (label[0] === 'vessel') {
      return t(`projections.vessel`).toUpperCase();
    } else if (label[0] === 'lesionKnown') {
      return t(`projections.lesionKnown`).toUpperCase();
    } else return t(`ResultView.${label[0]?.toLowerCase()}`);
  };

  const generalChipLabel = (label) => {
    if (!label[0] || label[0] == 'null' || label[0].toLowerCase() === 'not_eval') {
      return t(`Dashboard.not_eval`);
    }
    return t(`Dashboard.${label[0]}`, { defaultValue: `${label[0]}` }).toUpperCase();
  };

  const filterChipColor = (chipLabel) => {
    if (!chipLabel || !chipLabel[0]) {
      return theme.palette.accents.main;
    }
    if (chipLabel[0] === 'A' || chipLabel[0] === 'perfect' || chipLabel[0] === 'adequate') {
      return theme.palette.projections.perfect;
    }
    if (
      chipLabel[0] === 'B' ||
      chipLabel[0] === 'good' ||
      chipLabel[0] === 'none' ||
      chipLabel[0].includes('low')
    )
      return theme.palette.projections.good;
    if (
      chipLabel[0] === 'C' ||
      chipLabel[0] === 'moderate' ||
      chipLabel[0].includes('benign') ||
      chipLabel[0].includes('moderate')
    )
      return theme.palette.projections.moderate;
    if (
      chipLabel[0] === 'D' ||
      chipLabel[0] === 'inadequate' ||
      chipLabel[0].includes('malignant') ||
      chipLabel[0].includes('high')
    )
      return theme.palette.projections.insufficient;
    else return diagnosticsClassColor(chipLabel[0], theme) || theme.palette.accents.main;
  };

  const setfilterExaminations = useCallback(
    (dataKey, dataItem) => {
      const newFilter = { ...filter };
      newFilter[dataKey] = [dataItem];
      dispatch(actions.setFilter(newFilter));
      dispatch(actions.fetchExaminations());
    },

    [dispatch, filter],
  );

  const cardsToRender = [
    //  Examinations
    <Card
      style={{
        gridColumn: gui.landscape ? '1' : 'span 6',
        gridRow: '1',
      }}
      key='exams'>
      <ChartCard
        loading={loadingExams}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            <div
              style={{
                display: 'flex',
                gap: '1px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                flexWrap: 'wrap',
              }}>
              {t('Dashboard.examinations')}
              {'analyzed_3d' in filter && (
                <Chip
                  className={classes.smalleaturesFilter}
                  label={'3D'}
                  variant='outlined'
                  size='small'
                  onDelete={() => dispatch(actions.setFilterProperty(['analyzed_3d', null]))}
                  style={{
                    border: `1px solid ${theme.palette.accents.main}`,
                    color: theme.palette.accents.main,
                  }}
                />
              )}
              {'modality' in filter && filter.modality?.length > 0 && (
                <Chip
                  className={classes.smalleaturesFilter}
                  label={generalChipLabel(filter.modality)}
                  variant='outlined'
                  onDelete={() => dispatch(actions.setFilterProperty(['modality', null]))}
                  size='small'
                  style={{
                    border: `1px solid ${theme.palette.accents.main}`,
                    color: theme.palette.accents.main,
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: 'flex',
                gap: '1px',
                alignItems: 'center',
                justifyContent: 'space-evenly',
                width: '100%',
                flexWrap: 'nowrap',
                overflow: 'hidden',
              }}>
              {'screening' in filter && (
                <Chip
                  className={classes.smalleaturesFilter}
                  label={generalChipLabel(
                    filter.screening && filter.screening?.[0] === 'yes' ? ['screening'] : ['diagnostic'],
                  )}
                  variant='outlined'
                  onDelete={() => dispatch(actions.setFilterProperty(['screening', null]))}
                  size='small'
                  style={{
                    border: `1px solid ${filterChipColor(filter?.examinations)}`,
                    color: filterChipColor(filter?.examinations),
                  }}
                />
              )}

              {'postop' in filter && (
                <Chip
                  className={classes.smalleaturesFilter}
                  label={generalChipLabel(
                    filter.postop && filter.postop?.[0] === 'no' ? ['not_operated'] : ['postop'],
                  )}
                  variant='outlined'
                  onDelete={() => dispatch(actions.setFilterProperty(['postop', null]))}
                  size='small'
                  style={{
                    border: `1px solid ${filterChipColor(filter?.examinations)}`,
                    color: filterChipColor(filter?.examinations),
                  }}
                />
              )}
            </div>
          </div>
        }
        subheader={`${totalExaminations} ${t('Dashboard.examinations')}`}>
        <BarChartTwo
          chartdiv='bar_exams'
          theme={theme}
          darkState={gui.darkState}
          filterExaminations={setfilterExaminations}
        />
      </ChartCard>
    </Card>,

    // Quality
    <Card
      style={{
        gridColumn: gui.landscape ? '2' : '1 / 4',
        gridRow: gui.landscape ? '1' : '2',
      }}
      key='qual'>
      <ChartCard
        loading={loadingQuality}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {t('Dashboard.quality')}
            {'quality' in filter && (
              <Chip
                className={classes.featuresFilter}
                label={qualityChipLabel(filter.quality)}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty(['quality', null]))}
                size='small'
                style={{
                  border: `1px solid ${filterChipColor(filter?.quality)}`,
                  color: filterChipColor(filter?.quality),
                }}
              />
            )}
            {'study_quality' in filter && (
              <Chip
                className={classes.featuresFilter}
                label={qualityChipLabel(filter.study_quality)}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty(['study_quality', null]))}
                size='small'
                style={{
                  border: `1px solid ${filterChipColor(filter?.study_quality)}`,
                  color: filterChipColor(filter?.study_quality),
                }}
              />
            )}
          </div>
        }
        subheader={
          qualityData === 'projections'
            ? t('Dashboard.quality_per_projections')
            : t('Dashboard.quality_per_studies')
        }
        action={
          !('quality' in filter) &&
          !('study_quality' in filter) &&
          globalSettings?.quality_system?.overall_quality &&
          !(
            qualityStudiesData?.[0]?.category === 'Not evaluated' && qualityStudiesData?.[0]?.value === 100
          ) && <ButtonGroupQuality setQualityData={setQualityData} qualityData={qualityData} />
        }>
        <PieChart
          chartdiv='pie_quality'
          theme={theme}
          dataKey={qualityData === 'projections' ? 'pieQualityData' : 'pieStudyQualityData'}
          examinationListDrawer={examinationListDrawer}
          setFilterChartHandler={filterChartHandler}
          t={t}
        />
      </ChartCard>
    </Card>,

    // Density
    <Card
      style={{
        gridColumn: gui.landscape ? '3' : '4 / 7',
        gridRow: gui.landscape ? '1' : '2',
      }}
      key='dens'>
      <ChartCard
        loading={loadingDensity}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {t('Dashboard.density')}
            {'density' in filter && (
              <Chip
                className={classes.featuresFilter}
                label={`ACR ${generalChipLabel(filter.density)}`}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty(['density', null]))}
                size='small'
                style={{
                  border: `1px solid ${filterChipColor(filter?.density)}`,
                  color: filterChipColor(filter?.density),
                }}
              />
            )}
          </div>
        }
        subheader={density_module ? 'ACR' : t('Layout.premium')}>
        <PieChart
          chartdiv='pie_density'
          theme={theme}
          dataKey='pieDensityData'
          examinationListDrawer={examinationListDrawer}
          setFilterChartHandler={filterChartHandler}
          t={t}
          emptyChart={!loadingDensity && !density_module}
        />
      </ChartCard>
    </Card>,

    // Diagnostics
    <Card
      style={{
        gridColumn: gui.landscape ? '4' : '1 / 4',
        gridRow: gui.landscape ? '1' : '3',
      }}
      key='diagnostics'>
      <ChartCard
        loading={loadingOpacities}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {examinationListDrawer ? 'Diagnost.' : t('Dashboard.diagnostics')}
            {diagnosticCategory in filter && (
              <Chip
                className={classes.featuresFilter}
                label={diagnosticChipLabel(filter[diagnosticCategory])}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty([diagnosticCategory, null]))}
                size='small'
                style={{
                  border: `1px solid ${filterChipColor(filter[diagnosticCategory])}`,
                  color: filterChipColor(filter[diagnosticCategory]),
                }}
              />
            )}
          </div>
        }
        subheader={
          diagnostics_module
            ? diagnosticCategory === 'microcalc'
              ? t('ResultView.diagnosticsMicrocalc')
              : t('ResultView.diagnosticsLesions')
            : t('Layout.premium')
        }
        action={
          !(diagnosticCategory in filter) && (
            <ButtonGroupDiagnostics
              setDiagnosticCategory={setDiagnosticCategory}
              diagnosticCategory={diagnosticCategory}
              showButtons={diagnostics_microcalc_module && diagnostics_opacities_module}
            />
          )
        }>
        <PieChart
          chartdiv='pie_diagnostics'
          theme={theme}
          dataKey={diagnosticCategory}
          examinationListDrawer={examinationListDrawer}
          setFilterChartHandler={filterChartHandler}
          t={t}
          emptyChart={!loadingOpacities && !loadingMicrocalc && !diagnostics_module}
        />
      </ChartCard>
    </Card>,

    //  Risk
    <Card
      style={{
        gridColumn: gui.landscape ? '5' : '4 / 7',
        gridRow: gui.landscape ? '1' : '3',
      }}
      key='risk'>
      <ChartCard
        loading={loadingRisk}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {t('Dashboard.risk')}
            {'risk' in filter && (
              <Chip
                className={classes.featuresFilter}
                label={`${generalChipLabel(filter.risk)}`}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty(['risk', null]))}
                size='small'
                style={{
                  border: `1px solid ${filterChipColor(filter?.risk)}`,
                  color: filterChipColor(filter?.risk),
                }}
              />
            )}
          </div>
        }
        subheader={risk_module ? t('risk_management.risk_assessment') : t('Layout.premium')}>
        <PieChart
          chartdiv='pie_risk_data'
          theme={theme}
          dataKey='pieRiskData'
          examinationListDrawer={examinationListDrawer}
          t={t}
          setFilterChartHandler={filterChartHandler}
          emptyChart={!loadingRisk && !risk_module}
        />
      </ChartCard>
    </Card>,

    //PGMI - QUALITY FEATURES
    <Card
      style={{
        gridColumn: gui.landscape ? (histogram_chart ? 'span 4' : 'span 5') : 'span 6',
        gridRow: gui.landscape ? '2' : histogram_chart ? '4' : '4 / 6',
      }}
      key='bar_quality'>
      <ChartCard
        loading={loadingFeatures}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {t('Dashboard.quality_features')}
            {!!filter.featuresQuality && (
              <Chip
                className={classes.featuresFilter}
                label={`${filter.featuresQuality.label} ${filter.featuresQuality.value}`}
                variant='outlined'
                onDelete={() => dispatch(actions.setFilterProperty(['featuresQuality', null]))}
                size='small'
                style={{
                  border: `1px solid ${pgmiChipColor(filter?.featuresQuality?.value)}`,
                  color: pgmiChipColor(filter?.featuresQuality?.value),
                }}
              />
            )}
          </div>
        }
        action={
          <ButtonGroupQualityFeats
            onClick={setActiveQualityProjection}
            active_projection={qualityFeatProjection}
          />
        }
        subheader={
          !barQualityData[qualityFeatProjection] || barQualityData[qualityFeatProjection].length == 0
            ? t('Dashboard.no_features_placeholder')
            : qualityFeatProjection?.toUpperCase()
        }>
        <QualityFeaturesChart
          chartdiv='bar_quality'
          theme={theme}
          darkState={gui.darkState}
          setFeaturesQuality={featuresQualityHandler}
          qualityFeatProjection={qualityFeatProjection}
        />
      </ChartCard>
    </Card>,

    //  Quality Point histogram
    histogram_chart && (
      <Card
        style={{
          gridColumn: gui.landscape ? '1 span' : '1 / 7',
          gridRow: gui.landscape ? '2' : '7',
        }}
        key='histogram_quality'>
        <ChartCard
          loading={loadingDose}
          CardContentClass={classes.CardContent}
          title={t('Dashboard.quality_points')}
          uniqueStyle={true}
          subheader={formatAveragePerformance()}>
          <Histogram chartdiv='histogram_quality' theme={theme} darkState={gui.darkState} />
        </ChartCard>
      </Card>
    ),

    // Compression
    <Card
      style={{
        gridColumn: gui.landscape ? 'span 4' : 'span 6',
        gridRow: gui.landscape ? '3' : histogram_chart ? '5' : '6',
      }}
      key='performance_line'>
      <ChartCard
        loading={loadingCompression}
        CardContentClass={classes.CardContent}
        title={
          <div className={classes.title_header}>
            {t('Dashboard.compression')}
            {!!dates.compressionDates && (
              <Chip
                className={classes.featuresFilter}
                label={getChipLabel('compression')}
                variant='outlined'
                onDelete={() => dispatch(actions.setCompressionDates(null))}
                color='secondary'
                size='small'
              />
            )}
          </div>
        }
        action={
          <ButtonGroupCompression
            onClick={(index, btn_id) => setActiveProjCompression(index, btn_id)}
            cmp_projections={cmpProjections}
          />
        }>
        <MultilineChart
          chartdiv='chart_compression'
          active_projections={cmpProjections}
          theme={theme}
          darkState={gui.darkState}
          onBulletClick={onCompressionBulletClick}
          low_limit={compression_low}
          high_limit={compression_high}
          tolerance={compression_tolerance}
          dataKey='lineCompressionData'
          t={t}
        />
      </ChartCard>
    </Card>,

    //  Dose
    <Card
      style={{
        gridColumn: gui.landscape ? 'span 1' : 'span 6',
        gridRow: gui.landscape ? '3' : histogram_chart ? '6' : '7',
      }}
      key='dose'>
      <ChartCard
        loading={loadingDose}
        CardContentClass={classes.CardContent}
        title={t('Dashboard.dose')}
        subheader={averageDose ? `${t('Dashboard.average')} ${averageDose?.toFixed(0)}mGy` : ''}
        uniqueStyle={true}>
        <Histogram chartdiv='histogram_dose' theme={theme} darkState={gui.darkState} />
      </ChartCard>
    </Card>,
  ];

  return (
    <>
      <Helmet>
        <title>{t('b_box.pat_dashboard')}</title>
        <meta name='patient_dashboard' />
      </Helmet>

      <div className={gui.landscape ? classes.dashboard_general : classes.dashboard_responsive}>
        {cardsToRender}
      </div>
      <Copyright />
    </>
  );
};

export default Dashboard;
